import { TimelineMode } from '@src/editor';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { numericalInputStyles } from '../../styles/mainStyle';
import ColorDropdownSelect from '../common/ColorDropdownSelect';
import {
  CircleIcon,
  RectangleIcon,
  RoundedRectangleIcon,
} from '../common/icons';
import {
  basicCircleConfig,
  basicRectangleConfig,
  basicRoundedRectangleConfig,
} from '../shapeProcessor/shapeDefaults';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface ShapeSettingsProps {
  activeElement: ElementState | null | undefined;
  hideActionButtons?: boolean;
  editorStore: EditorStoreLike;
}

enum Align {
  leftAlign = 'Left Align',
  midAlign = 'Middle Align',
  rightAlign = 'Right Align',
}

enum ShapeType {
  roundedRectangle = 'roundedRectangle',
  rectangle = 'rectangle',
  circle = 'circle',
}

export const AlignValues = {
  [Align.leftAlign]: '0%',
  [Align.midAlign]: '50%',
  [Align.rightAlign]: '100%',
};

enum Colors {
  black = 'black',
  white = 'white',
  red = 'red',
  green = 'green',
  orange = 'orange',
  blue = 'blue',
  yellow = 'yellow',
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 3,
  minimumFractionDigits: 0,
});

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 20px;
  /* No bottom spacing here: the panel card's Body already contributes
     --spacing-md, which is the whole of what the Speakers panel uses and is
     the pattern every tab now follows. Adding more here double-counted it. */
  position: relative;
  height: 100%;
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
`;

const Heading = styled.div`
  width: 100%;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  margin-top: 10px;
`;

const Item = styled.div``;

const PositionContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

const AddShapeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  aspect-ratio: 1;
  padding: 16px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease;

  svg {
    opacity: 0.7;
    transition: opacity 120ms ease;
  }

  &:hover {
    border-color: var(--text-muted);
    background: var(--surface-raised, var(--surface-elevated));
    svg {
      opacity: 1;
    }
  }

  &:active {
    scale: 0.97;
  }
`;

const ShapeWrapper = styled.div`
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  gap: 10px;
`;

const AddShapeButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const AddShapeElementButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: var(--btn-h-lg);
  padding: 0 var(--btn-px-lg);
  background: var(--primary);
  color: var(--primary-foreground);
  border: none;
  border-radius: var(--radius-button);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  transition: background 120ms ease;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:active:not(:disabled) {
    background: var(--primary-pressed);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HorizontalLine = styled.div`
  background-color: var(--border);
  width: 100%;
  height: 1px;
  margin-top: 20px;
`;

export const ShapeSettings = observer(
  forwardRef<HTMLDivElement, ShapeSettingsProps>(props => {
    const videoCreator = useVideoCreatorStore();
    const { editorStore } = props;
    const { source } = props.activeElement || {};
    const config = videoCreator.shapeProducer.getShapeConfig();
    const textWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (source) {
        videoCreator.shapeProducer.setShapeConfig(source);
      }
    }, [source]);

    useEffect(() => {
      if (!source) {
        videoCreator.shapeProducer.setShapeConfig({
          ...config,
          time: videoCreator.time,
        });
      }
    }, [videoCreator.time]);

    const modifyProperty = (property: Record<string, unknown>) => {
      if (editorStore?.editor && props.activeElement?.source?.id) {
        const success = editorStore.editor.commandUpdateElement(
          props.activeElement.source.id,
          property,
        );
        if (!success) {
          console.warn(`Failed to update shape element properties:`, property);
        }
      }
    };

    const handleGetInputValue = async (
      property: string,
      value: string,
      valueFormatted: string,
    ) => {
      if (!value) {
        return;
      }
      modifyProperty({
        [property]: valueFormatted,
      });
    };

    const renderFullShapeSettings = () => {
      if (!props.activeElement) return null; // runtime + TS narrow
      const activeElement = props.activeElement; // narrowed ElementState
      return (
        <>
          <PositionContent>
            <Item>
              <PropertyCaption>Width</PropertyCaption>
              <PropertyNumericalInput
                propertyName="width"
                defaultValue={config.width || 0}
                unit={config.shape_type === ShapeType.circle ? 'vmin' : '%'}
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
            <Item>
              <PropertyCaption>Height</PropertyCaption>
              <PropertyNumericalInput
                propertyName="height"
                defaultValue={config.height || 0}
                unit={config.shape_type === ShapeType.circle ? 'vmin' : '%'}
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
            <Item>
              <PropertyCaption>X Position</PropertyCaption>
              <PropertyNumericalInput
                propertyName="x"
                defaultValue={config.x || 0}
                unit="%"
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
            <Item>
              <PropertyCaption>Y Position</PropertyCaption>
              <PropertyNumericalInput
                propertyName="y"
                defaultValue={config.y || 0}
                unit="%"
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
          </PositionContent>

          <HorizontalLine />
          <PositionContent>
            <Item>
              <PropertyCaption>Fill Color</PropertyCaption>
              <ColorDropdownSelect
                action={(propertyName, value) =>
                  modifyProperty({
                    [propertyName]: value,
                  })
                }
                propertyName="fill_color"
                defaultValue={config.fill_color || Colors.black}
              />
            </Item>
            <Item>
              <PropertyCaption>Opacity</PropertyCaption>
              <PropertyNumericalInput
                propertyName="opacity"
                defaultValue={config.opacity || 100}
                unit="%"
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
          </PositionContent>
          <HorizontalLine />
          <Heading>Border</Heading>
          <PositionContent>
            <Item>
              <PropertyCaption>Stroke Color</PropertyCaption>
              <ColorDropdownSelect
                action={(propertyName, value) =>
                  modifyProperty({
                    [propertyName]: value,
                  })
                }
                propertyName="stroke_color"
                defaultValue={config.stroke_color || Colors.black}
              />
            </Item>
            <Item>
              <PropertyCaption>Stroke Width</PropertyCaption>
              <PropertyNumericalInput
                propertyName="stroke_width"
                defaultValue={config.stroke_width || 0}
                unit="vmin"
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
          </PositionContent>
          <HorizontalLine />
          <PositionContent>
            <Item>
              <PropertyCaption>Start</PropertyCaption>
              <PropertyNumericalInput
                propertyName="time"
                defaultValue={config.time || 0}
                unit="s"
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
            <Item>
              <PropertyCaption>End</PropertyCaption>
              <PropertyNumericalInput
                propertyName="time"
                defaultValue={formatter.format(
                  (parseFloat(config.time) || 0) +
                    (parseFloat(config.duration) || 4),
                )}
                unit="s"
                customStyles={numericalInputStyles}
                getInputValue={async (property: string, value: string) => {
                  const duration = formatter.format(
                    parseFloat(value) - parseFloat(config.time) || 0,
                  );
                  modifyProperty({ duration });
                }}
              />
            </Item>
            <Item>
              <PropertyCaption>Length</PropertyCaption>
              <PropertyNumericalInput
                propertyName="duration"
                defaultValue={config.duration || 4}
                unit="s"
                customStyles={numericalInputStyles}
                getInputValue={handleGetInputValue}
              />
            </Item>
          </PositionContent>

          <HorizontalLine />

          <ElementAnimationSettings
            activeElement={activeElement}
            editorStore={editorStore}
          />
          <GenericAnimationSettings
            activeElement={activeElement}
            editorStore={editorStore}
          />
        </>
      );
    };

    // Helper function to create text element using headless editor or fallback to legacy
    const createShapeElement = async (shapeType: ShapeType) => {
      // Track analytics like the original method
      analytics.track('create_shape_element', {
        storyId: videoCreator.story?.id,
        storyTitle: videoCreator.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
      });

      // Get text producer config for smart positioning and aspect ratio defaults
      let currentConfig;
      if (shapeType === ShapeType.rectangle) {
        currentConfig = basicRectangleConfig;
      } else if (shapeType === ShapeType.roundedRectangle) {
        currentConfig = basicRoundedRectangleConfig;
      } else if (shapeType === ShapeType.circle) {
        currentConfig = basicCircleConfig;
      }

      // Smart positioning logic (from original produceText)
      // const activeElement = videoCreator.getActiveElement();

      // Prepare properties for the shape element
      const shapeProps = {
        ...currentConfig,
        time: videoCreator.time,
      };

      console.log('Creating shape element with props:', shapeProps);

      // Create element using headless editor command
      const element = editorStore.editor.commandCreateShapeElement(
        shapeProps,
        undefined,
        TimelineMode.DEFAULT, // Use default timeline mode, ripple mode is not enabled yet
      );

      if (element) {
        // Select the newly created element so user can see it highlighted
        console.log('Selecting newly created shape element:', element.id);
        editorStore.selectElement(element.id);

        // Also update legacy VideoCreatorStore selection for UI compatibility
        console.log('Also setting active elements in VideoCreatorStore');
        await videoCreator.setActiveElements(element.id);
      }
    };

    const onAddNewShapeElement = async () => {
      await videoCreator.setActiveElements();
    };

    return (
      <Main>
        {!props.activeElement && (
          <ShapeWrapper ref={textWrapperRef}>
            <AddShapeButton
              type="button"
              onClick={() => createShapeElement(ShapeType.roundedRectangle)}
            >
              <RoundedRectangleIcon height="40" width="40" />
            </AddShapeButton>
            <AddShapeButton
              type="button"
              onClick={() => createShapeElement(ShapeType.rectangle)}
            >
              <RectangleIcon height="40" width="40" />
            </AddShapeButton>
            <AddShapeButton
              type="button"
              onClick={() => createShapeElement(ShapeType.circle)}
            >
              <CircleIcon height="40" width="40" />
            </AddShapeButton>
          </ShapeWrapper>
        )}

        {props.activeElement && (
          <>
            {!props.hideActionButtons && (
              <>
                <AddShapeButtonContainer>
                  <AddShapeElementButton
                    type="button"
                    onClick={onAddNewShapeElement}
                  >
                    Add New Shape Element
                  </AddShapeElementButton>
                </AddShapeButtonContainer>
                <HorizontalLine />
              </>
            )}
            {renderFullShapeSettings()}
          </>
        )}
      </Main>
    );
  }),
);
